'use client'

import { SidebarHeader } from '@/shared/layouts/AdminLayout/SidebarHeader'
import { SidebarMenu } from '@/shared/layouts/AdminLayout/SidebarMenu'
import React, { useState, useEffect } from 'react'
import { HamburgerMenu } from '@/shared/layouts/AdminLayout/HamburgerMenu'
import { HeaderActions } from '@/shared/layouts/AdminLayout/HeaderActions'
import { useIsMobile } from '@workspace/ui/hooks/use-mobile'
import { Search } from 'lucide-react'
import { Input } from '@workspace/ui/components/Textfield'
import { GlobalSearchDialog } from '@/shared/components/dialogs/GlobalSearchDialog'

import { refreshUser } from '@/shared/store/auth.store'
import { Button } from '@workspace/ui/components/Button'

export function AdminLayout({ children }: { children: React.ReactNode }) {
    refreshUser()
    const isMobile = useIsMobile({ breakpoint: 1024 })
    const [searchDialogOpen, setSearchDialogOpen] = useState(false)

    // Global keyboard shortcut: Cmd/Ctrl + K
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault()
                setSearchDialogOpen(true)
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [])

    return (
        <>
            <div className="min-h-screen">
                {/* Sticky header */}
                <div className="sticky top-0 z-20 bg-background">
                    {/* Desktop header */}
                    {!isMobile && (
                        <div className="container max-w-[1400px] mx-auto max-lg:hidden h-16 flex items-center justify-between px-6">
                            <SidebarHeader />
                            <div
                                className="relative w-80 cursor-pointer"
                                onClick={() => setSearchDialogOpen(true)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault()
                                        setSearchDialogOpen(true)
                                    }
                                }}
                            >
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-[18px] h-[18px] pointer-events-none" />
                                <Input
                                    placeholder="Tìm kiếm dự án, tài liệu"
                                    className="pl-10 h-10 text-sm bg-background rounded-lg cursor-pointer"
                                    readOnly
                                />
                            </div>
                            <HeaderActions />
                        </div>
                    )}

                    {/* Mobile header */}
                    {isMobile && (
                        <div className="lg:hidden h-14 flex items-center gap-2 pr-3 pl-2">
                            <HamburgerMenu />
                            <div className="flex-1" />
                            <Button variant="ghost" onClick={() => setSearchDialogOpen(true)} size="icon">
                                <Search size={20} />
                            </Button>
                            <HeaderActions />
                        </div>
                    )}

                    {/* Horizontal menu - sticky below header */}
                    {!isMobile && (
                        <div className="max-lg:hidden bg-background">
                            <div className="container px-6 max-w-[1400px] mx-auto">
                                <SidebarMenu />
                            </div>
                        </div>
                    )}
                </div>

                {/* Main content */}
                <div className="container max-w-[1400px] mx-auto">
                    <div className="px-6 py-4 max-lg:px-3 max-lg:py-2">{children}</div>
                </div>
            </div>

            {/* Global Search Dialog */}
            <GlobalSearchDialog open={searchDialogOpen} onOpenChange={setSearchDialogOpen} />
        </>
    )
}
